import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Request from "common/Request";
import NVStackedAreaChart, {
  tooltipContentGeneratorForUnits,
  formats,
} from "common/graphs/NVStackedAreaChart";
import NVLineChart from "common/graphs/NVLineChart";
import RangeSliderInput from "common/RangeSliderInput";
import * as d3 from "d3";
import ReportContextControls from "../ReportContextControls";
import { retrieveRange, getSampleSize } from "common/api";
import { useSearchParams } from "react-router-dom";
import { toObjectLiteral } from "common/utils";
import BqnContext from "common/BqnContext";
import { useScreenSize } from "common/hooks";
import { hoursAsPeriod } from "common/constants";

const isInsideRange =
  ({ from, to }, field = "time") =>
    (item) =>
      from <= item[field] && item[field] <= to;

const getFieldsOf = (groupName) => {
  const getGroupFields = ([item, ...rest]) =>
    item === undefined
      ? []
      : groupName in item
        ? item[groupName].map(({ name }) => ({ name, label: name }))
        : getGroupFields([...rest]);
  return getGroupFields;
};

const getFieldsLanes = (groupName, valueName) => (items) =>
  items.map(({ time, ...content }) =>
    Object.fromEntries([
      ["time", time],
      ...(content[groupName] || []).map(({ name, ...values }) => [
        name,
        values[valueName],
      ]),
    ])
  );

const UsageGraphLayout = styled.div`
  h4 {
    text-align: center;
    &.align-left {
      text-align: left;
    }
  }
`;
const CenteredMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 500px;
`;
const NoDataFound = ({ message = "No data found" }) => (
  <CenteredMessage>
    <h4>{message}</h4>
  </CenteredMessage>
);

const sumOfValuesGiven = keys => item =>
  d3.sum(keys, key => item[key] ?? 0);

const mostRelevant = (bucket, keys) =>
  bucket[d3.maxIndex(bucket, sumOfValuesGiven(keys))];

const squash = ([first, ...rest], keys) =>
  rest.length === 0
    ? first
    : ({
      ...mostRelevant([first, ...rest], keys),
      time: first.time,
    });

export const downsampling = ({ data, keys = [], sampleSize }) => {
  let result = [];
  let bucket = [];
  for (let i = 0; i < data.length; i += 1) {
    if (i > 0 && i % sampleSize === 0) {
      result.push(squash(bucket, keys));
      bucket = [{ ...data[i] }];
    } else {
      bucket.push({ ...data[i] });
    }
  }
  //Flush remaining trailing items at the end of the loop
  if (bucket.length > 0) {
    result.push(squash(bucket, keys));
  }
  return result;
};

const ReportResult = ({
  Totals = NoTotals,
  from = "speeds",
  itemsFrom,
  itemsValueFrom,
  graphTitle = null,
  yAxisUnits,
  yAxisPrecision,
  fixedYRange,
  range: wholeRange = null,
  context = {},
  noDataMessage = undefined,
  participants = undefined,
  onSeriesInspect = undefined,
  stacked = true,
  tooltipContentGenerator = undefined,
  ...result
}) => {
  const data = result[from];
  const [range, setRange] = useState(wholeRange);
  const onChange = (value) => setRange(value);
  const onInspect = onSeriesInspect === undefined
    ? undefined
    : (series) => onSeriesInspect(series, context);

  if (data === undefined || data.length === 0) {
    return <NoDataFound message={noDataMessage} />
  }

  return (
    <>
      <UsageGraph
        items={data}
        range={range}
        itemsFrom={itemsFrom}
        itemsValueFrom={itemsValueFrom}
        graphTitle={graphTitle}
        yAxisUnits={doUnits(yAxisUnits, context)}
        yAxisPrecision={yAxisPrecision}
        yAxisFormat={yAxisPrecision === 0 ? formats.noDecimals : undefined}
        fixedYRange={fixedYRange}
        participants={participants}
        context={context}
        onSeriesInspect={onInspect}
        noDataMessage={noDataMessage}
        tooltipContentGenerator={tooltipContentGenerator}
        stacked={stacked}
      />
      <RangeSliderInput
        {...wholeRange}
        className="full-width margin-t-20 margin-b-20"
        onChange={onChange}
      />
      <Totals {...result} />
    </>
  );
};

const BigGraph = styled.div`
  min-height: 12cm;
  & svg {
    min-height: 500px;
  }
  @media print {
    width: 100% !important;
    overflow: hidden;
  }
`;
const NoTotals = () => console.log("no totals configured ") || null;
const alongsideRange = (range, items) =>
  range === null || items.length === 0
    ? items
    : extentToRangeLimits(range, items);

const fixToTime = (moment, item) =>
({
  ...item,
  time: moment,
})

const extentToRangeLimits = (range, items) => [
  fixToTime(range.from, items.at(0)),
  ...items.filter(isInsideRange(range)),
  fixToTime(range.to, items.at(-1)),
];

const evaluateContext = (context, from) =>
  typeof from === "function" ? from(context) : from;

const asNameLabel = (names) => names.map((name) => ({ name, label: name }));

const doUnits = (param, context) =>
  typeof param === 'function' ? param(context) : param

const UsageGraph = ({
  items,
  range,
  participants,
  itemsFrom = "services",
  itemsValueFrom = "speed",
  graphTitle = null,
  yAxisUnits = "Mbps",
  yAxisFormat = undefined,
  context = undefined,
  onSeriesInspect = undefined,
  noDataMessage = undefined,
  stacked = true,
  tooltipContentGenerator = tooltipContentGeneratorForUnits,
  fixedYRange = undefined,
}) => {
  const { width: windowWidth } = useScreenSize();
  const POINTS_PER_TILE_CHART = windowWidth / 2;
  const { hours } = context;
  const [itemsToRender, setItemsToRender] = useState(null);

  useEffect(() => {
    setItemsToRender(
      alongsideRange(
        range,
        getFieldsLanes(
          itemsFrom,
          evaluateContext(context, itemsValueFrom),
          participants
        )(items)
      )
    );
  }, [range]);

  if (!itemsToRender || itemsToRender.length === 0) {
    return <NoDataFound message={noDataMessage} />
  }
  const fields = participants === undefined
    ? getFieldsOf(itemsFrom)(items)
    : asNameLabel(participants);
  const keys = fields.map(({ name }) => name);
  const sampleSize = getSampleSize(range, POINTS_PER_TILE_CHART, hoursAsPeriod[hours]);

  const Chart = stacked === true ? NVStackedAreaChart : NVLineChart;
  return (
    <UsageGraphLayout className="group margin-t-20">
      {graphTitle === null ? null : <h4>{graphTitle}</h4>}
      <BigGraph className="big graph">
        <Chart
          items={downsampling({ data: itemsToRender, keys, sampleSize })}
          xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
          xField="time"
          fields={fields}
          yAxisUnits={yAxisUnits}
          yAxisFormat={yAxisFormat}
          tooltipContentGenerator={
            tooltipContentGenerator(
              yAxisUnits,
              yAxisFormat
            )
          }
          yAxisTopGap={0.05}
          onSeriesInspect={onSeriesInspect}
          fixedYRange={fixedYRange}
        />
      </BigGraph>
    </UsageGraphLayout>
  );
};

const ReportRequest = ({
  children,
  actions,
  provider,
  volumeOrRatio,
  hours,
  directions,
  categories,
  filter,
  output,
  target,
  policyType,
  ...rest
}) => {
  const {
    bqnId,
    mode,
  } = useContext(BqnContext);
  const [request, setRequest] = useState(null);
  const [range, setRange] = useState(null);
  const doLoad = () => {
    const doingRetrieval = retrieveRange(hours)
      .then((range) => setRange(range) || range)
      .then(() =>
        provider({
          volumeOrRatio,
          hours,
          directions,
          categories,
          filter,
          output,
          target,
          policyType,
          range,
          mode,
          bqnId
        })
      );
    setRequest(doingRetrieval);
  };
  useEffect(() => {
    doLoad();
    return actions.recv("do-load", doLoad);
  }, [
    hours,
    volumeOrRatio,
    directions,
    categories,
    filter,
    output,
    output,
    target,
    policyType,
    bqnId
  ]);
  const context = {
    hours,
    directions,
    categories,
    filter,
    output,
    target,
    policyType,
    volumeOrRatio,
  };
  return request === null ? null : (
    <Request during={request}>
      {(result) => children({ ...result, ...rest, range, context })}
    </Request>
  );
};

const _defaultContext = {
  hours: 24,
  interval: 10,
  categories: 20,
  directions: "all",
  output: "ip-addresses",
  target: { subsId: null, addr: null },
  policyType: "rate",
};

export const buildInitialContext = (settings, searchParams, defaults) =>
  Object.fromEntries(
    Object.entries(defaults).map(([field, defaultValue]) => [
      field,
      (field in settings && settings[field].selected) ||
      (field === 'target' ? { subsId: searchParams.subsId, addr: searchParams.addr } : searchParams[field]) ||
      defaultValue,
    ])
  );
const ReportContext = ({
  provider,
  actions,
  enabled,
  settings = {},
  ...rest
}) => {
  let [searchParams] = useSearchParams();
  const [context, setContext] = useState(
    buildInitialContext(settings, toObjectLiteral(searchParams), _defaultContext)
  );
  const updateContext = ({
    volumeOrRatio,
    hours,
    directions,
    categories,
    filter,
    output,
    target,
    policyType,
  }) =>
    setContext((prev) => ({
      ...prev,
      ...(volumeOrRatio === undefined ? {} : { volumeOrRatio }),
      ...(hours === undefined ? {} : { hours }),
      ...(directions === undefined ? {} : { directions }),
      ...(categories === undefined ? {} : { categories }),
      ...(filter === undefined ? {} : { filter }),
      ...(output === undefined ? {} : { output }),
      ...(target === undefined ? {} : { target }),
      ...(policyType === undefined ? {} : { policyType }),
    }));
  return (
    <>
      <ReportContextControls
        {...context}
        settings={settings}
        onChange={updateContext}
        enabled={enabled}
        hasTwoWeeks
      >
        <ReportRequest
          provider={provider}
          {...context}
          actions={actions}
          {...rest}
        >
          {(result) => <ReportResult context={context} {...result} {...rest} />}
        </ReportRequest>
      </ReportContextControls>
    </>
  );
};

export default ReportContext;
